import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertTriangle } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useDateLocale } from '@/hooks/useDateLocale';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { useTranslation } from 'react-i18next';
import type { Device, Position } from '@/lib/traccar-api';

interface AlarmsCardProps {
  devices: Device[];
  positions: Position[];
}

const ALARM_LABELS: Record<string, { label: string; severity: 'error' | 'warning' | 'info' }> = {
  sos: { label: 'SOS', severity: 'error' },
  vibration: { label: 'Vibration', severity: 'warning' },
  movement: { label: 'Movement', severity: 'warning' },
  lowspeed: { label: 'Low Speed', severity: 'info' },
  overspeed: { label: 'Overspeed', severity: 'error' },
  fallDown: { label: 'Fall Down', severity: 'error' },
  lowPower: { label: 'Low Power', severity: 'warning' },
  lowBattery: { label: 'Low Battery', severity: 'warning' },
  fault: { label: 'Fault', severity: 'error' },
  powerOff: { label: 'Power Off', severity: 'warning' },
  powerOn: { label: 'Power On', severity: 'info' },
  door: { label: 'Door', severity: 'info' },
  lock: { label: 'Lock', severity: 'info' },
  unlock: { label: 'Unlock', severity: 'info' },
  geofence: { label: 'Geofence', severity: 'warning' },
  geofenceEnter: { label: 'Geofence Enter', severity: 'info' },
  geofenceExit: { label: 'Geofence Exit', severity: 'warning' },
  gpsAntennaCut: { label: 'GPS Antenna Cut', severity: 'error' },
  accident: { label: 'Accident', severity: 'error' },
  tow: { label: 'Tow', severity: 'error' },
  idle: { label: 'Idle', severity: 'info' },
  highRpm: { label: 'High RPM', severity: 'warning' },
  hardAcceleration: { label: 'Hard Acceleration', severity: 'warning' },
  hardBraking: { label: 'Hard Braking', severity: 'warning' },
  hardCornering: { label: 'Hard Cornering', severity: 'warning' },
  laneChange: { label: 'Lane Change', severity: 'info' },
  fatigueDriving: { label: 'Fatigue Driving', severity: 'error' },
  powerCut: { label: 'Power Cut', severity: 'error' },
  powerRestored: { label: 'Power Restored', severity: 'info' },
  jamming: { label: 'Jamming', severity: 'error' },
  temperature: { label: 'Temperature', severity: 'warning' },
  parking: { label: 'Parking', severity: 'info' },
  bonnet: { label: 'Bonnet', severity: 'warning' },
  footBrake: { label: 'Foot Brake', severity: 'info' },
  fuelLeak: { label: 'Fuel Leak', severity: 'error' },
  tampering: { label: 'Tampering', severity: 'error' },
  removing: { label: 'Removing', severity: 'error' },
};

const getSeverityColor = (severity: 'error' | 'warning' | 'info') => {
  switch (severity) {
    case 'error':
      return 'bg-destructive text-destructive-foreground';
    case 'warning':
      return 'bg-[hsl(45,93%,47%)] text-black';
    case 'info':
      return 'bg-primary text-primary-foreground';
  }
};

export const AlarmsCard = ({ devices, positions }: AlarmsCardProps) => {
  const { t } = useTranslation();
  const dateFnsLocale = useDateLocale();
  const deviceMap = new Map(devices.map(d => [d.id, d]));
  
  // Filter positions that have an alarm attribute
  const alarmedPositions = positions
    .filter(p => p.attributes?.alarm)
    .map(p => ({
      position: p,
      device: deviceMap.get(p.deviceId),
      alarm: p.attributes.alarm as string,
    }))
    .filter(item => item.device)
    .sort((a, b) => new Date(b.position.fixTime).getTime() - new Date(a.position.fixTime).getTime());

  const alarmCount = alarmedPositions.length;

  return (
    <Card className="h-full flex flex-col overflow-hidden">
      <CardHeader className="py-2 pl-4 pr-14 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className={`p-1.5 rounded-lg ${alarmCount > 0 ? 'bg-destructive/10' : 'bg-muted'}`}>
              <AlertTriangle className={`h-3.5 w-3.5 ${alarmCount > 0 ? 'text-destructive' : 'text-muted-foreground'}`} />
            </div>
            <div>
              <CardTitle className="text-sm">{t('dashboard.activeAlarms')}</CardTitle>
              <p className="text-xs text-muted-foreground">
                {alarmCount === 0 ? t('common.noData') : `${alarmCount} ${t('devices.title').toLowerCase()}`}
              </p>
            </div>
          </div>
          {alarmCount > 0 && (
            <Badge variant="destructive" className="text-xs">{alarmCount}</Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="py-2 px-4 flex-1 min-h-0">
        {alarmCount === 0 ? (
          <div className="flex items-center justify-center h-full text-muted-foreground text-xs">
            All devices operating normally
          </div>
        ) : (
          <ScrollArea className="h-full">
            <div className="space-y-1.5">
              {alarmedPositions.map((item) => {
                const alarmInfo = ALARM_LABELS[item.alarm] || { label: item.alarm, severity: 'warning' as const };
                return (
                  <div
                    key={`${item.position.deviceId}-${item.position.id}`}
                    className="flex items-center justify-between p-1.5 rounded bg-muted/50 hover:bg-muted transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium truncate">{item.device?.name}</p>
                      <p className="text-[10px] text-muted-foreground">
                        {formatDistanceToNow(new Date(item.position.fixTime), { addSuffix: true, locale: dateFnsLocale })}
                      </p>
                    </div>
                    <Badge className={`${getSeverityColor(alarmInfo.severity)} text-[10px] px-1.5 py-0`}>
                      {alarmInfo.label}
                    </Badge>
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
};